import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const CORE = ["student_name", "college", "fee", "paid", "balance", "status"];

export function ShortTermColumnsDialog({ columns, onClose, onSaved }) {
  const [cols, setCols] = useState(columns.map((c) => ({ ...c, _rid: c.key })));
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setCols((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (i, dir) => setCols((cs) => { const n = [...cs]; const j = i + dir; if (j < 0 || j >= n.length) return cs; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setBusy(true);
    try { await api.put("/short-term/columns", { columns: cols.map(({ _rid, ...c }) => c) }); toast.success("Columns saved"); onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="st-columns-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Grid columns</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-auto pr-1">
          {cols.map((c, i) => (
            <div key={c._rid} className="flex items-center gap-2" data-testid={`st-colrow-${c._rid}`}>
              <input data-testid={`st-col-label-${c._rid}`} value={c.label} onChange={(e) => set(i, { label: e.target.value })} className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
              {CORE.includes(c.key) || c.type === "computed" || c.type === "status" ? (
                <span className="w-24 text-[11px] text-slate-400">{c.type === "computed" ? "Fee − Paid" : "required"}</span>
              ) : (
                <select value={c.type} onChange={(e) => set(i, { type: e.target.value })} className="w-24 px-2 py-1.5 rounded-lg border border-slate-200 text-xs">
                  <option value="text">Text</option><option value="number">Number</option><option value="date">Date</option>
                </select>
              )}
              <button type="button" onClick={() => move(i, -1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" data-testid={`st-col-remove-${c._rid}`} disabled={CORE.includes(c.key)} onClick={() => setCols((cs) => cs.filter((_, j) => j !== i))} className="p-1 text-slate-300 hover:text-rose-600 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button type="button" data-testid="st-col-add" onClick={() => setCols((cs) => [...cs, { key: "", _rid: `new${Date.now().toString(36)}${cs.length}`, label: "New column", type: "text" }])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary"><Plus className="h-4 w-4" /> Add column</button>
        <button data-testid="st-columns-save" onClick={save} disabled={busy} className="w-full px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-60">{busy ? "Saving…" : "Save columns"}</button>
      </DialogContent>
    </Dialog>
  );
}
